import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Icon } from '@gumtree/ui-library';
import { navigate } from '@gumtree/shell/src/common/common-actions';
import { components } from 'react-select';
import { getNonFalsyUrlSearchParams } from '@gumtree/shared/src/util/url-params';

import { SrpState, SrpUseDispatch } from '../../../reducers';
import { updateLocationListThunk } from '../../../reducers/location-list';
import {
    StyledIcon,
    StyledLocationField,
    StyledLocationItem,
    StyledOptionContainer,
    StyledRadiusBlock,
    StyledSearchResults,
    StyledSelect,
    StyledUpdateButton,
} from './location-block.style';
import type { Filter } from '../../../types';
import { trackGA4FiltersDiffThunk } from '../../../reducers/current-filter-states';

const CustomOption = (props) => (
    <StyledOptionContainer>
        {props.isSelected && <StyledIcon type="check" />}
        <components.Option {...props} />
    </StyledOptionContainer>
);

const CustomDropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
        <Icon type="spinner" />
    </components.DropdownIndicator>
);

const navigateToLocation = async (
    currentFilterStates: Filter,
    locationValue: string,
    selectedDistanceValue: number | undefined,
    dispatch: SrpUseDispatch
) => {
    const url = getNonFalsyUrlSearchParams(currentFilterStates);
    const updatedSearchFilters = {
        ...(locationValue && { location: locationValue }),
        ...(selectedDistanceValue && { distance: selectedDistanceValue }),
    };
    dispatch(
        trackGA4FiltersDiffThunk({
            updatedSearchFilters,
            searchFilters: updatedSearchFilters,
        })
    );

    url.set('search_location', locationValue);
    selectedDistanceValue && url.set('distance', selectedDistanceValue.toString());
    dispatch(navigate(`/search?${url.toString()}`));
};

export const LocationBlock = () => {
    const dispatch = useDispatch() as SrpUseDispatch;
    const {
        searchBar,
        resultsPage: { currentFilterStates, distanceFilter },
        locationList: { locationList },
    } = useSelector((data: SrpState) => data);

    const [locationValue, setLocationValue] = useState(searchBar.location.value);
    const [isLocationList, setIsLocationList] = useState(false);
    const [selectedDistance, setSelectedDistance] = useState<{
        value: number;
        label: string;
    }>(() => {
        const { value, displayValue } =
            distanceFilter.find((item) => item.selected) ?? distanceFilter[0];

        return { value, label: displayValue };
    });

    const handleRunLocationSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setLocationValue(value);

        if (value.length >= 2) {
            dispatch(updateLocationListThunk(value));
            setIsLocationList(true);
        }
    };

    const handleLocationKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            navigateToLocation(
                currentFilterStates,
                locationValue,
                selectedDistance?.value,
                dispatch
            );
        }
    };

    const handleSelectLocation = (locationName: string) => () => {
        setLocationValue(locationName);
        setIsLocationList(false);
    };

    const distanceSelectOptions = distanceFilter.map(({ value, displayValue }) => ({
        value,
        label: displayValue,
    }));

    const handleDistanceChange = (option) => setSelectedDistance(option);
    const isDisabled = (option) =>
        distanceFilter.find((item) => item.value === option.value)?.disabled ?? false;

    const handleRunFilter = () => {
        navigateToLocation(currentFilterStates, locationValue, selectedDistance?.value, dispatch);
    };

    return (
        <>
            <StyledLocationField>
                <Icon type="beacon" size="medium" className="location-input-icon" />
                <Field
                    id="location-field-value"
                    placeholder="Postcode or location"
                    value={locationValue}
                    onInput={handleRunLocationSearch}
                    onKeyDown={handleLocationKeyDown}
                />
            </StyledLocationField>
            {isLocationList ? (
                <StyledSearchResults>
                    {locationList.slice(0, 3).map((item) => (
                        <StyledLocationItem
                            key={item.name}
                            onClick={handleSelectLocation(item.name)}
                        >
                            <Icon type="beacon" size="medium" className="location-search-icon" />
                            {item.name}
                        </StyledLocationItem>
                    ))}
                </StyledSearchResults>
            ) : null}
            <StyledRadiusBlock>
                <StyledSelect
                    classNamePrefix="react-select"
                    instanceId="location"
                    components={{
                        Option: CustomOption,
                        DropdownIndicator: CustomDropdownIndicator,
                    }}
                    placeholder=""
                    value={selectedDistance}
                    options={distanceSelectOptions}
                    onChange={handleDistanceChange}
                    isOptionDisabled={isDisabled}
                />
                <StyledUpdateButton
                    display="primary"
                    label="Update"
                    onClick={handleRunFilter}
                />
            </StyledRadiusBlock>
        </>
    );
};
